import React from 'react';
import { Link } from 'react-router-dom';
import './Terms.css';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';

const Terms = () => {
  return (
    <div className="terms-page">
      <Header />

      <div className="terms-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last updated: July 15, 2025</p>

        <p className="intro">
          Thank you for choosing <strong>NowTarot</strong> ("<strong>Service</strong>", "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>"). These Terms of Service ("<strong>Terms</strong>") govern your access to and use of our website, applications, and any related services (collectively, the "<strong>Platform</strong>"). Please read them carefully.
        </p>

        <div className="terms-section">
          <h2>1. Service Description</h2>
          <p>
            NowTarot is an <strong>AI‑generated entertainment experience</strong> that provides responses to user‑submitted questions in a tarot and fortune‑telling style. All outputs are computer‑generated and are <strong>for entertainment and personal reflection only</strong>.
          </p>
        </div>

        <div className="terms-section">
          <h2>2. Eligibility</h2>
          <p>
            You must be <strong>at least 18 years old</strong> and reside in a jurisdiction where the Platform is legally available. By using the Service, you represent that you meet these requirements.
          </p>
        </div>

        <div className="terms-section">
          <h2>3. Entertainment‑Only & No Professional Advice</h2>
          <ul>
            <li><strong>No Guarantees.</strong> The Service makes no claim of accuracy, certainty, or predictive power. Responses are speculative and may be incorrect or incomplete.</li>
            <li><strong>No Professional Advice.</strong> Outputs <strong>do not constitute</strong> medical, mental‑health, legal, financial, investment, or other professional advice. You should consult a qualified professional for decisions in these areas.</li>
          </ul>
        </div>

        <div className="terms-section">
          <h2>4. Prohibited Questions & Content</h2>
          <p>You agree <strong>NOT</strong> to use the Platform to seek or disseminate:</p>
          <ol>
            <li><strong>Medical advice</strong> (e.g., diagnosis, treatment, prognosis).</li>
            <li><strong>Legal advice</strong> (e.g., litigation outcomes, contract interpretation).</li>
            <li><strong>Financial or investment advice</strong> (e.g., stock tips, tax strategies).</li>
            <li>Content that is illegal, defamatory, harassing, hateful, or otherwise violates applicable laws.</li>
          </ol>
          <p>We reserve the right to filter, refuse, or remove any questions or content at our sole discretion.</p>
        </div>

        <div className="terms-section">
          <h2>5. Account Registration & Data We Collect</h2>
          <p>When you create or use an account, we collect and store:</p>
          <ul>
            <li><strong>Personal Information:</strong> Email address, full name, phone number, IP address, and country.</li>
            <li><strong>Usage Information:</strong> Questions asked and corresponding AI‑generated answers.</li>
          </ul>
          <p>For details on how we use and protect this data, please review our Privacy Policy.</p>
        </div>

        <div className="terms-section">
          <h2>6. Payments & Refund Policy</h2>
          <ul>
            <li><strong>Billing.</strong> Payments are processed by third‑party providers. You agree to pay all fees disclosed at checkout.</li>
            <li><strong>Refund Requests.</strong> You may request a refund through our web app within <strong>7 days</strong> of purchase. Approved refunds will be issued to your original payment method within a reasonable period.</li>
            <li><strong>Chargebacks.</strong> Initiating an unjustified chargeback constitutes a breach of these Terms and may result in account suspension.</li>
          </ul>
        </div>

        <div className="terms-section">
          <h2>7. Intellectual Property</h2>
          <p>
            The Platform and all related materials are owned by us or our licensors. Subject to these Terms, we grant you a <strong>limited, personal, non‑exclusive, non‑transferable</strong> license to access and use the Platform for personal, non‑commercial purposes.
          </p>
        </div>

        <div className="terms-section">
          <h2>8. User Content License</h2>
          <p>
            By submitting questions or other content ("User Content"), you grant us a worldwide, royalty‑free, sublicensable license to use, reproduce, and create derivative works from your User Content solely for the purpose of operating, maintaining, and improving the Service. We will not publish or publicly display your User Content without your explicit consent.
          </p>
        </div>

        <div className="terms-section">
          <h2>9. Modifications & Updates</h2>
          <p>
            We may modify the Platform or these Terms at any time. We will notify you of material changes via the Platform or email. Continued use after changes take effect constitutes acceptance.
          </p>
        </div>

        <div className="terms-section">
          <h2>10. Suspension & Termination</h2>
          <p>
            We may suspend or terminate your access without notice if you violate these Terms or applicable laws, or if required by our payment or hosting partners.
          </p>
        </div>

        <div className="terms-section">
          <h2>11. Disclaimers</h2>
          <p>
            THE SERVICE IS PROVIDED <strong>"AS IS"</strong> WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON‑INFRINGEMENT.
          </p>
        </div>

        <div className="terms-section">
          <h2>12. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, <strong>our total liability</strong> for any claim arising out of or relating to the Service <strong>will not exceed</strong> the greater of (a) the amount you paid us in the 12 months preceding the claim or (b) USD 100.
          </p>
        </div>

        <div className="terms-section">
          <h2>13. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless NowTarot and the people who run it from any claims, damages, liabilities, and expenses arising out of your use of the Platform or violation of these Terms.
          </p>
        </div>

        <div className="terms-section">
          <h2>14. Governing Law & Dispute Resolution</h2>
          <p>
            These Terms are governed by the laws of the <strong>State of Delaware</strong>, USA, without regard to conflict of law principles. Any dispute shall be resolved through <strong>binding arbitration</strong> in Wilmington, Delaware, pursuant to the rules of the American Arbitration Association. You waive any right to participate in a class‑action lawsuit.
          </p>
        </div>

        <div className="terms-section">
          <h2>15. Severability</h2>
          <p>
            If any provision of these Terms is held unenforceable, the remaining provisions will remain in full force and effect.
          </p>
        </div>

        <div className="terms-section">
          <h2>16. Contact Us</h2>
          <p>
            For questions about these Terms, please contact us through our <Link to="/support">support page</Link>.
          </p>
        </div>

        <div className="terms-section">
          <h2>17. Entire Agreement</h2>
          <p>
            These Terms and any referenced policies constitute the <strong>entire agreement</strong> between you and us, superseding all prior agreements or understandings regarding the Service.
          </p>
        </div>

        <p className="terms-footer">
          <strong>By continuing to use the Platform, you acknowledge that you have read, understood, and agreed to these Terms of Service.</strong>
        </p>
      </div>

      <Footer />
    </div>
  );
};

export default Terms; 